"use client";

import Image from "next/image";
import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { usePathname } from "next/navigation";
import parse from 'html-react-parser';

// Define a type for the FAQ data
interface Faq {
  title: string;
  article: string;
  image?: string;
}

const FaqSection: React.FC = () => {
  const [faqs, setFaqs] = useState<Faq[]>([]); // Store all fetched FAQs
  const [faqsToShow, setFaqsToShow] = useState<Faq[]>([]); // Store the FAQs to display
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const pathname = usePathname();
  let rawSlug = pathname?.split("/").pop() || "home";
  if (pathname && pathname.includes('/country-domains/')) {
    rawSlug = "country-domains";
  }
  // Dynamically import Bootstrap's JS to avoid SSR issues
  useEffect(() => {
    // @ts-expect-error: We're using dynamic imports here, which may not have type definitions
    import("bootstrap/dist/js/bootstrap.bundle.min.js");
  }, []);

  // Fetch FAQs data from the API
  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await fetch('/api/faq', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rawSlug
          }),
        });
        const data = await response.json(); 
        const articles = data.articles;
        if (articles?.result == "error") {
          throw new Error("Failed to fetch data");
        }
        setFaqs(articles); // Save all fetched data to 'faqs'
        setFaqsToShow(articles.slice(0, 6)); // Initially load 6 items
        setLoading(false);
      } catch (error: unknown) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError("An unknown error occurred");
        }
        setLoading(false);
      }
    };

    fetchFaqs();
  }, [rawSlug]);

  const loadMoreFaqs = () => {
    if (faqsToShow.length >= faqs.length) return; // Limit to the total number of FAQs
    setFaqsToShow((prev) => [
      ...prev,
      ...faqs.slice(faqsToShow.length, faqsToShow.length + 6), // Add next 6 items
    ]);
  };

  if (loading) {
    return <section id="faq-section" className="container-lg">
      <div className="text-center">
        <h1 className="fw-semibold mt-4">
          <span className="fw-bold fs-2 textHad text-center mb-4">
            Frequently Asked Questions
          </span>
        </h1>
        <div>Loading...</div>
      </div>
    </section>;
  }

  if (error) {
    return <section id="faq-section" className="container-lg">
      <div className="text-center">
        <h1 className="fw-semibold mt-4">
          <span className="fw-bold fs-2 textHad text-center mb-4">
            Frequently Asked Questions
          </span>
        </h1>
        <h6 className="display-7 text-center textHad">Error : {error}</h6>
      </div>
    </section>;
  }

  if (faqs.length === 0) {
    return <section id="faq-section" className="container-lg">
      <div className="text-center">
        <h1 className="fw-semibold mt-4">
          <span className="fw-bold fs-2 textHad text-center mb-4">
            Frequently Asked Questions
          </span>
        </h1>
        <h6 className="display-7 text-center textHad">No FAQs available.</h6>
      </div>
    </section>;
  }

  return (
    <section id="faq-section" className="container-lg">
      <div className="text-center">
        <h1 className="fw-semibold mt-4">
          <span className="fw-bold fs-2 textHad text-center mb-4">
            Frequently Asked Questions
          </span>
        </h1>
      </div>
      <div className="d-flex justify-content-center">
      <div className="col-md-12 col-12">
      <div className="accordion mt-4" id="faq-accordion">
        {faqsToShow.map((item : Faq, i :number) => (
          <div
            key={i}
            className="accordion-item my-3"
          >
            <h2 className="accordion-header">
              <button
                className="accordion-button collapsed fw-bold"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target={`#collapse${i}`}
                aria-expanded="false"
                aria-controls={`collapse${i}`}
              >
                {parse(item.title || `Question ${i + 1}`)}
              </button>
            </h2>
            <div
              id={`collapse${i}`}
              className="accordion-collapse collapse"
              data-bs-parent="#faq-accordion"
            >
              <div className="accordion-body">
                <div>{parse(item.article || 'Answer not available')}</div>
                {/* Display image if available */}
                {item.image && (
                  <div className="mt-3">
                    <Image
                      src={item.image}
                      alt={`Image for FAQ ${i + 1}`}
                      className="img-fluid"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      </div>
      </div>
      {faqsToShow.length < faqs.length && ( // Check if there are more FAQs to load
        <div className="text-center">
          <Button variant="primary" size="lg" onClick={loadMoreFaqs}  className='button'>
            <span>Load More</span>
          </Button>
        </div>
      )}
    </section>
  );
};

export default FaqSection;
